// import React, { useState, useRef, useEffect } from "react";
// import { createPortal } from "react-dom";
// import { UserMinus } from "lucide-react";
// import RemoveMemberModal from "./RemoveMemberModal";


// const MemberActionMenu = ({ member, onRemoveAccess }) => {
//   const [isOpen, setIsOpen] = useState(false);
//   const [isModalOpen, setIsModalOpen] = useState(false);
//   const [menuPosition, setMenuPosition] = useState({ top: 0, left: 0 });
//   const menuRef = useRef(null);
//   const buttonRef = useRef(null);


//   useEffect(() => {
//     if (isOpen && buttonRef.current) {
//       const rect = buttonRef.current.getBoundingClientRect();
//       setMenuPosition({
//         top: rect.bottom + window.scrollY + 8,
//         left: rect.right + window.scrollX - 192
//       });
//     }
//   }, [isOpen]);


//   useEffect(() => {
//     const handleClickOutside = (event) => {
//       if (
//         menuRef.current &&
//         !menuRef.current.contains(event.target) &&
//         buttonRef.current &&
//         !buttonRef.current.contains(event.target)
//       ) {
//         setIsOpen(false);
//       }
//     };


//     if (isOpen) {
//       document.addEventListener("mousedown", handleClickOutside);
//     }


//     return () => {
//       document.removeEventListener("mousedown", handleClickOutside);
//     };
//   }, [isOpen]);


//   const handleRemoveClick = () => {
//     setIsOpen(false);
//     setIsModalOpen(true);
//   };


//   const handleConfirmRemove = async (member) => {
//     await onRemoveAccess(member);
//   };

//   // Check if the member role is admin
//   const isAdmin = member.role === "admin";

//   return (
//     <>
//       <button
//         ref={buttonRef}
//         onClick={() => setIsOpen(!isOpen)}
//         disabled={isAdmin}
//         className={`p-1 rounded-full transition-colors ${
//           isAdmin 
//             ? "text-gray-300 cursor-not-allowed" 
//             : "text-gray-400 hover:text-[#18101C] hover:bg-gray-100"
//         }`}
//       >
//         <svg
//           width="18"
//           height="18"
//           viewBox="0 0 24 24"
//           fill="none"
//           xmlns="http://www.w3.org/2000/svg"
//         >
//           <circle cx="12" cy="5" r="2" fill="currentColor" />
//           <circle cx="12" cy="12" r="2" fill="currentColor" />
//           <circle cx="12" cy="19" r="2" fill="currentColor" />
//         </svg>
//       </button>


//       {isOpen && !isAdmin && createPortal(
//         <div
//           ref={menuRef}
//           className="w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z- animate-in fade-in zoom-in-95 duration-100"
//           style={{
//             position: 'absolute',
//             top: `${menuPosition.top}px`,
//             left: `${menuPosition.left}px`
//           }}
//         >
//           <button
//             onClick={handleRemoveClick}
//             className="w-full px-4 py-2.5 text-left text-sm !text-[#E21919BF] hover:bg-red-50 flex items-center gap-2.5 transition-colors"
//           >
//             <UserMinus size={16} color="#E21919BF"/>
//             Remove Access
//           </button>
//         </div>,
//         document.body
//       )}


//       <RemoveMemberModal
//         isOpen={isModalOpen}
//         onClose={() => setIsModalOpen(false)}
//         onConfirm={handleConfirmRemove}
//         member={member}
//       />
//     </>
//   );
// };


// export default MemberActionMenu;
import React, { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { UserMinus } from "lucide-react";

// ``locked`` is set while a bulk removal is running. Two removal flows at once
// would each open their own reassignment modal against the same members, so the
// row menu stands down until the bulk one finishes.
const MemberActionMenu = ({ member, onRemoveAccess, locked = false }) => {
  const [isOpen, setIsOpen] = useState(false);
  // 1. Start as null instead of { top: 0, left: 0 }
  const [menuPosition, setMenuPosition] = useState(null); 
  const menuRef = useRef(null);
  const buttonRef = useRef(null);

  // 2. Calculate position inside the click handler BEFORE opening
  const handleToggle = () => {
    if (!isOpen && buttonRef.current) {
      const rect = buttonRef.current.getBoundingClientRect();
      setMenuPosition({
        top: rect.bottom + window.scrollY + 8,
        left: rect.right + window.scrollX - 192
      });
    }
    setIsOpen(!isOpen);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        menuRef.current &&
        !menuRef.current.contains(event.target) &&
        buttonRef.current &&
        !buttonRef.current.contains(event.target)
      ) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [isOpen]);

  // Which dialog to raise depends on whether this person owns anything, and only
  // the parent knows that (it has to ask the server). So the menu just reports
  // the intent and lets MembersPage put up the right one — either the plain
  // confirm or the reassignment modal, never both.
  const handleRemoveClick = () => {
    setIsOpen(false);
    onRemoveAccess(member);
  };

  const isAdmin = member.role === "admin";
  const unavailable = isAdmin || locked;

  return (
    <>
      <button
        ref={buttonRef}
        onClick={handleToggle}
        disabled={unavailable}
        title={locked && !isAdmin ? "Finish the bulk removal first" : undefined}
        className={`p-1 rounded-full transition-colors ${
          unavailable
            ? "text-gray-300 cursor-not-allowed"
            : "text-gray-400 hover:text-[#18101C] hover:bg-gray-100"
        }`}
      >
        <svg
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          xmlns="http://www.w3.org/2000/svg"
        >
          <circle cx="12" cy="5" r="2" fill="currentColor" />
          <circle cx="12" cy="12" r="2" fill="currentColor" />
          <circle cx="12" cy="19" r="2" fill="currentColor" />
        </svg>
      </button>

      {/* 3. Make sure menuPosition is not null before rendering to prevent any jumping */}
      {isOpen && menuPosition && !isAdmin && createPortal(
        <div
          ref={menuRef}
          // Fixed a dangling 'z-' tailwind typo here
          className="w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-[9999] animate-in fade-in zoom-in-95 duration-100"
          style={{
            position: 'absolute',
            top: `${menuPosition.top}px`,
            left: `${menuPosition.left}px`
          }}
        >
          <button
            onClick={handleRemoveClick}
            className="w-full px-4 py-2.5 text-left text-sm !text-[#E21919BF] hover:bg-red-50 flex items-center gap-2.5 transition-colors"
          >
            <UserMinus size={16} color="#E21919BF"/>
            Remove Access
          </button>
        </div>,
        document.body
      )}

    </>
  );
};

export default MemberActionMenu;
